import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Snackbar,
    Alert,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { createCategory, updateCategory, getCategoryById } from "../../Apis/CategoriesApi";

const CreateCategory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const editMode = Boolean(id);

    // State variables
    const [name, setName] = useState("");
    const [order, setOrder] = useState("");
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [status, setStatus] = useState(true);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // Load category data if in edit mode
    useEffect(() => {
        if (editMode) {
            getCategoryById(id)
                .then((res) => {
                    const cat = res?.data || res;
                    setName(cat.name || "");
                    setOrder(cat.order || "");
                    setPreview(cat.imageUrl || null);
                    setStatus(cat.Status !== undefined ? cat.Status : true);
                })
                .catch(() => setErrorMsg("Failed to load category."));
        }
    }, [editMode, id]);

    // Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim() || !order || (!editMode && !image)) {
            setErrorMsg(
                "Please fill all fields" + (!editMode ? " and upload an image." : ".")
            );
            return;
        }

        const formData = new FormData();
        formData.append("name", name);
        formData.append("order", order);
        formData.append("status", status ? "true" : "false");
        if (image) formData.append("categoryImages", image);

        try {
            setLoading(true);
            if (editMode) {
                await updateCategory(id, formData);
            } else {
                await createCategory(formData);
            }
            setSuccess(true);
            setTimeout(() => navigate("/getadmincategory"), 1500);
        } catch (error) {
            setErrorMsg(editMode ? "Failed to update category." : "Failed to create category.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", px: 2, py: 4 }}>
            <Paper sx={{ width: "100%", maxWidth: 500, p: 4, borderRadius: 3, boxShadow: 3 }}>
                <Typography variant="h5" gutterBottom align="center">
                    {editMode ? "Update Category" : "Create Category"}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Category Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        margin="normal"
                        size="small"
                        required
                    />
                    <TextField
                        label="Order"
                        type="number"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        fullWidth
                        margin="normal"
                        size="small"
                        required
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={status}
                                onChange={(e) => setStatus(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Active"
                    />
                    <Button
                        variant="contained"
                        component="label"
                        fullWidth
                        sx={{ mt: 2, mb: 2 }}
                    >
                        Upload Image
                        <input type="file" hidden onChange={handleImageChange} />
                    </Button>

                    {preview && (
                        <Box sx={{ mb: 2, textAlign: "center" }}>
                            <Typography variant="body2">Preview:</Typography>
                            <img
                                src={preview}
                                alt="preview"
                                style={{ width: 120, marginTop: 8, borderRadius: 8 }}
                            />
                        </Box>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                    >
                        {loading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Category" : "Create Category")}
                    </Button>
                </form>

                {/* Success Snackbar */}
                <Snackbar
                    open={success}
                    autoHideDuration={3000}
                    onClose={() => setSuccess(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity="success" variant="filled">
                        {editMode ? "Category updated successfully!" : "Category created successfully!"}
                    </Alert>
                </Snackbar>

                {/* Error Snackbar */}
                <Snackbar
                    open={!!errorMsg}
                    autoHideDuration={4000}
                    onClose={() => setErrorMsg("")}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity="error" variant="filled">
                        {errorMsg}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

export default CreateCategory;
